import {
  FC,
  useEffect,
  useState,
} from 'react';
import { useCookies } from 'react-cookie';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

import Button from 'components/Button';
import { ConsentValue, Mixpanel } from 'mixpanel';
import { makeId } from 'utils/helpers';
import useLanguage from 'hooks/useLanguage';

type Language = 'English' | 'German' | 'French';

const COOKIE_NAME = 'consent-agreement';

const options: Array<{ value: Language; label: string }> = [
  {
    value: 'English',
    label: '🇬🇧',
  },
  {
    value: 'German',
    label: '🇩🇪',
  },
  {
    value: 'French',
    label: '🇫🇷',
  },
];

const CookieConsentModal: FC = () => {
  const [cookies, setCookie] = useCookies([COOKIE_NAME]);
  const [hidden, setHidden] = useState<boolean>(!!cookies[COOKIE_NAME]);
  const { blueLang, greenLang } = useLanguage();
  const defaultLang = (blueLang || greenLang || 'English') as Language;
  const [lang, setLang] = useState<Language>(defaultLang);
  const { t } = useTranslation(undefined, { lng: lang });

  useEffect(() => {
    setLang(blueLang as Language);
  }, [blueLang]);

  useEffect(() => {
    setLang(greenLang as Language);
  }, [greenLang]);

  const handleConsent = (value: ConsentValue) => {
    setCookie(COOKIE_NAME, value, {
      path: '/',
      sameSite: 'strict',
      secure: true,
      expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
    });

    Mixpanel.setConsent(value);
    setHidden(true);
  };

  useEffect(() => {
    const id = makeId(7);

    Mixpanel.subscribeToState(id, (state) => {
      setHidden(state.disabled);
    });

    return () => Mixpanel.unsubscribeFromState(id);
  }, []);

  if (hidden) {
    return null;
  }

  return (
    createPortal(
      <div className='z-[1000] fixed left-0 w-full bg-white py-8 bottom-0 border-t border-t-[#cbcbcb]'>
        <div className='px-8 mx-auto max-w-7xl flex justify-between items-center gap-6 sm:pt-[38px] sm:flex-col sm:relative'>
          <div className='sm:absolute sm:top-[-20px] sm:left-8'>
            <Select
              options={options}
              className='min-w-[38px] w-[38px] cursor-pointer'
              defaultValue={options.find((option) => option.value === defaultLang)}
              menuPlacement='top'
              components={{
                IndicatorSeparator: () => null,
                IndicatorsContainer: () => null,
              }}
              value={options.find((option) => option.value === lang)}
              onChange={(e) => {
                if (e?.value) {
                  setLang(e.value as Language);
                }
              }}
            />
          </div>
          <p>
            <span>
              {t('cookie.consent')}
            </span>
            <Link to='https://gdpr.eu/cookies/' target='_blank' className='pl-1 underline'>
              {t('cookie.learnMore')}
            </Link>
          </p>

          <div className='flex gap-2 sm:flex-wrap sm:justify-center'>
            <Button onClick={() => handleConsent('all')} className='px-4 whitespace-nowrap'>
              {t('cookie.acceptAll')}
            </Button>

            <Button onClick={() => handleConsent('necessary')} className='px-4 whitespace-nowrap'>
              {t('cookie.onlyNecessary')}
            </Button>

            <Button
              onClick={() => handleConsent('none')}
              btnType='secondary'
              className='px-4 whitespace-nowrap'
            >
              {t('cookie.decline')}
            </Button>
          </div>
        </div>
      </div>,
      document.body,
    )
  );
};

export default CookieConsentModal;
